/**
 * Onboarding after signup, in the /demo look (spec 2026-10-15-onboarding-demo-look):
 * invoice details → card → what happens now. Then the app.
 *
 * Server steps stay 1..6 (6 = finished) because live rows are mid-flow:
 *   2 = invoice details (required) · 3 = card (skippable) · 4 = done.
 * Advancing PERSISTS the step (PATCH /api/brand/onboarding-step), fire and
 * forget; going back does not, and the route rejects a lower step anyway.
 * Rows left on retired step 5 land on the last screen.
 *
 * @module web/screens/OnboardingScreen
 */
import { useEffect, useState } from "react";
import { get, patch, put } from "../api/client.ts";
import { useBrand } from "../state/BrandContext.tsx";
import { resolveVaultReturn, startVault } from "../state/vault.ts";
import { mountWhopLoader, whopEmbedAttributes } from "./app/whopEmbed.ts";
import { DemoQuoteCard, readDemoQuote, Shell } from "./onboarding/Shell.tsx";
import { FLOW_STEPS } from "./SignUpScreen.tsx";
import { TOTAL_STEPS, FIRST_STEP, LAST_SLIDE } from "./onboarding/Progress.tsx";

export function OnboardingScreen() {
  const { obStep, setObStep, refresh } = useBrand();
  const step = Math.min(Math.max(obStep, FIRST_STEP), LAST_SLIDE);
  const [quote] = useState(() => readDemoQuote());

  function advance(): void {
    setObStep(step + 1);
    void patch("/api/brand/onboarding-step", { step: step + 1 }).catch(() => {});
  }
  const back = step > FIRST_STEP ? () => setObStep(step - 1) : undefined;
  async function finish(): Promise<void> {
    void patch("/api/brand/onboarding-step", { step: TOTAL_STEPS }).catch(() => {});
    await refresh();
  }

  if (step === 2) return <Shell screen="onboarding-screen" step={4} of={FLOW_STEPS}><DemoQuoteCard quote={quote} /><BillingStep onDone={advance} /></Shell>;
  if (step === 3) return <Shell screen="onboarding-screen" step={5} of={FLOW_STEPS}><CardStep onDone={advance} onBack={back} /></Shell>;
  return <Shell screen="onboarding-screen"><DoneStep hasQuote={!!quote} onDone={() => void finish()} /></Shell>;
}

// ---------------------------------------------------------------- invoice details

interface Billing { company: string; street: string; postcode: string; city: string; country: string; vatId: string; invoiceEmail: string }

/** Where Aditor's clients are; the rest pick from the long tail below. */
const COUNTRIES = ["DE", "AT", "CH", "NL", "BE", "LU", "FR", "IT", "ES", "PT", "IE", "GB", "DK", "SE", "NO", "FI", "PL", "CZ", "US", "CA", "AU", "NZ", "HK", "SG", "AE", "IL",
  "BG", "HR", "CY", "EE", "GR", "HU", "LV", "LT", "MT", "RO", "SK", "SI", "IS", "LI", "MC", "TR", "UA", "RS", "JP", "KR", "TW", "IN", "ZA", "BR", "MX", "AR", "SA", "QA"];

function guessCountry(): string {
  const region = (navigator.language.split("-")[1] ?? "").toUpperCase();
  return COUNTRIES.includes(region) ? region : "DE";
}

function BillingStep({ onDone }: { onDone: () => void }) {
  const { brand } = useBrand();
  const [b, setB] = useState<Billing>({ company: brand?.name ?? "", street: "", postcode: "", city: "", country: guessCountry(), vatId: "", invoiceEmail: brand?.email ?? "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const names = new Intl.DisplayNames(["en"], { type: "region" });

  // Someone coming back to this step sees what they saved, not empty boxes.
  useEffect(() => {
    void get<{ details: (Billing & { vatId: string | null }) | null }>("/api/brand/billing-details")
      .then((r) => { if (r.details) setB({ ...r.details, vatId: r.details.vatId ?? "" }); })
      .catch(() => {});
  }, []);

  const set = (k: keyof Billing) => (e: { target: { value: string } }) => setB({ ...b, [k]: e.target.value });
  const complete = !!(b.company.trim() && b.street.trim() && b.postcode.trim() && b.city.trim() && b.country && b.invoiceEmail.trim());

  async function save(): Promise<void> {
    if (!complete || busy) return;
    setBusy(true);
    setError(null);
    try {
      await put("/api/brand/billing-details", b);
      onDone();
    } catch (e) {
      // Keep every field; say what to fix.
      setError(e instanceof Error ? e.message : "Could not save. Try again.");
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className="ob2-h1">Invoice <span>details.</span></h1>
      <p className="ob2-sub">What goes on your invoices. Takes 30 seconds.</p>
      <form className="ob2-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <label className="ob2-field"><span>Company</span>
          <input id="ob-company" className="ob2-input" autoComplete="organization" value={b.company} onChange={set("company")} /></label>
        <label className="ob2-field"><span>Street and number</span>
          <input id="ob-street" className="ob2-input" autoComplete="address-line1" value={b.street} onChange={set("street")} /></label>
        <div className="ob2-row">
          <label className="ob2-field"><span>Postcode</span>
            <input id="ob-postcode" className="ob2-input" autoComplete="postal-code" value={b.postcode} onChange={set("postcode")} /></label>
          <label className="ob2-field"><span>City</span>
            <input id="ob-city" className="ob2-input" autoComplete="address-level2" value={b.city} onChange={set("city")} /></label>
        </div>
        <label className="ob2-field"><span>Country</span>
          <select id="ob-country" className="ob2-input" autoComplete="country" value={b.country} onChange={set("country")}>
            {COUNTRIES.map((c) => <option key={c} value={c}>{names.of(c)}</option>)}
          </select></label>
        <label className="ob2-field"><span>VAT ID <em>· optional</em></span>
          <input id="ob-vat" className="ob2-input" placeholder="DE123456789" value={b.vatId} onChange={set("vatId")} /></label>
        <label className="ob2-field"><span>Send invoices to</span>
          <input id="ob-invoice-email" className="ob2-input" type="email" autoComplete="email" value={b.invoiceEmail} onChange={set("invoiceEmail")} /></label>
        {error && <p className="ob2-error" role="alert">{error}</p>}
        <div className="ob2-actions">
          <button type="submit" className="dm-cta dm-cta-lg" disabled={!complete || busy}>{busy ? "Saving…" : "Continue"}</button>
        </div>
      </form>
    </>
  );
}

// ---------------------------------------------------------------- card

/** Same Whop rail as the Wallet; skippable, because a brief can wait for it. */
function CardStep({ onDone, onBack }: { onDone: () => void; onBack: (() => void) | undefined }) {
  const [session, setSession] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "saved" | "off">("loading");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    startVault()
      .then((v) => {
        if (v.hasCardOnFile) setState("saved");
        else if (v.configured && v.sessionId) { setSession(v.sessionId); setState("ready"); }
        else setState("off");
      })
      .catch(() => setState("off"));
  }, []);
  // After the div exists: the loader mounts whatever is in the DOM when it runs.
  useEffect(() => { if (session) mountWhopLoader(); }, [session]);

  async function check(): Promise<void> {
    if (busy) return;
    setBusy(true);
    const r = await resolveVaultReturn(); // one lookup per tap, never a loop
    setBusy(false);
    if (r.ok) setState("saved");
    else setMsg(r.message);
  }

  return (
    <>
      <h1 className="ob2-h1">{state === "saved" ? <>Card <span>saved.</span></> : <>Add your <span>card.</span></>}</h1>
      <p className="ob2-sub">
        {state === "saved" ? "Every brief from here is one tap." : "Charged only when you confirm a brief, at the price you saw. No subscription."}
      </p>
      {state === "ready" && session && (
        <div className="ob2-card"><div id="whop-embed-target" {...whopEmbedAttributes(session, `${window.location.origin}/onboarding?vault=return`)} /></div>
      )}
      {state === "loading" && <p className="ob2-note">Opening the secure card form…</p>}
      {state === "off" && <p className="ob2-note">Card payments are not switched on for your account yet. We will set it up with you; you can carry on.</p>}
      {msg && <p className="ob2-error" role="alert">{msg}</p>}
      <div className="ob2-actions" style={{ width: "100%" }}>
        {state === "ready" && <button type="button" className="dm-cta dm-cta-lg" onClick={() => void check()} disabled={busy}>{busy ? "Checking…" : "I've added my card"}</button>}
        {(state === "saved" || state === "off") && <button type="button" className="dm-cta dm-cta-lg" onClick={onDone}>Continue</button>}
        {state === "ready" && <button type="button" className="dm-quiet" onClick={onDone}>Add it later</button>}
        {onBack && state !== "ready" && <button type="button" className="dm-quiet" onClick={onBack}>Back</button>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- done

function DoneStep({ hasQuote, onDone }: { hasQuote: boolean; onDone: () => void }) {
  return (
    <>
      <h1 className="ob2-h1">You're <span>in.</span></h1>
      <p className="ob2-sub">Here is how every ad works from now on.</p>
      <ol className="ob2-perks">
        <li><i>1</i><div><b>Brief it</b>{hasQuote ? "Your priced script is already in. Check it and send." : "Paste a script or describe the ad."}</div></li>
        <li><i>2</i><div><b>See the price, tap once</b>Priced on the spot. Your card is charged only when you confirm.</div></li>
        <li><i>3</i><div><b>Ready in 48 h</b>Our best-performing editors get your work first.</div></li>
      </ol>
      <div className="ob2-actions" style={{ width: "100%" }}>
        <button type="button" className="dm-cta dm-cta-lg" onClick={onDone}>Let's go</button>
      </div>
    </>
  );
}
